import { openModal } from "~/common/utils/ModalService";
import { type AddToCartHookProps, type AddToCartSubmitData } from "~/features/addToCart/hooks/useAddToCartForm";
import { type AddToCartSubmitHandler } from "~/features/addToCart/components/AddToCartProcess/AddToCartWizardShared";
import {
  createAddToCartCloseIntentAdapter,
  createEditPartCloseIntentAdapter,
} from "~/features/addToCart/AddToCartServiceCompat";
import { AddToCartWizard } from "~/features/addToCart/components/AddToCartProcess/AddToCartWizard";
import { addToCartApi } from "~/features/addToCart/redux/addToCart/actions";
import { EditPartWizard } from "~/features/addToCart/components/AddToCartProcess/EditPartWizard";
import { Permission, userHasAllPermissions } from "~/domains/user/utils/permissions";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import type { AddToCartSource } from "~/app/analytics/types";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";

async function openAddToCartWizard(
  hookProps: Omit<AddToCartHookProps, "editMode"> & {
    restrictionQuery: string;
    source?: AddToCartSource;
    showSuccessDrawer?: boolean;
    position?: number;
    onSuccessCallback?: () => void;
    onCancelCallback?: () => void;
  },
  context: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  },
) {
  const addToCartSource: AddToCartSource = hookProps.source ?? "unknown";
  const onSubmit = (submitData: AddToCartSubmitData) => {
    return addToCartApi({
      productCode: submitData.derived.descriptor.base.code,
      restrictionQuery: hookProps.restrictionQuery,
      submitData,
      addToCartSource,
      context,
      position: hookProps.position,
      showSuccessDrawer: hookProps.showSuccessDrawer,
      onSuccessCallback: hookProps.onSuccessCallback,
    });
  };

  if (userHasAllPermissions(Permission.AVAILABILITY, Permission.PRICE)) {
    return openModal("add-to-cart-modal", (close) => (
      <AddToCartWizard
        onSubmit={onSubmit}
        editMode={false}
        onRequestClose={createAddToCartCloseIntentAdapter(close, hookProps.onCancelCallback)}
        {...hookProps}
      />
    ));
  }

  // Non customer flow. Here we don't know availabilities or prices, so we just perform an add to cart call with all possible data faked

  const fakedSubmitData: AddToCartSubmitData = {
    formValues: {
      quantity: hookProps.initialData?.quantity ?? 1,
      priceReference: getInitialPriceReference(),
      selectedPackCode: INVALID_PACKAGE_CODE,
      salePriceEnabled: false,
      optionalParts: {},
    },
    derived: {
      selectedPack: null,
      selectedPackaging: null,
      netPrice: null,
      totalQuantity: 1,
      descriptor: {} as ProductDescriptor,
    },
    optionalParts: {},
  };

  return addToCartApi({
    productCode: hookProps.productCode,
    restrictionQuery: hookProps.restrictionQuery,
    submitData: fakedSubmitData,
    addToCartSource,
    context,
  });
}

async function openEditPartWizard(
  hookProps: Omit<AddToCartHookProps, "editMode"> & { restrictionQuery: string; onSubmit: AddToCartSubmitHandler },
) {
  return openModal("edit-cart-modal", (close) => {
    return <EditPartWizard editMode={true} onRequestClose={createEditPartCloseIntentAdapter(close)} {...hookProps} />;
  });
}

export const AddToCartService = {
  openAddToCartWizard,
  openEditPartWizard,
};
